import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { setActionPayload, setActionType } from "../../../features/authorizationSlice";
import { ACTION_TYPES } from "../../../app/actionTypes";
import { formatAccount } from "../../../features/ValueFormater";
import { roleAuth } from "../../../utils/functionAuthorization";
import AutoConversionControl from "./AutoConversionControl";
import ImagePicker from "./ImagePicker";
import InfoRows from "./InfoRows";
import PaymentFeesOptions from "./PaymentFeesOptions";
import SectionTabs from "../SectionTabs";

function SkeletonRows() {
  return (
    <div className="new-merchant-information__skeleton">
      {Array.from({ length: 7 }).map((_, index) => (
        <div className="new-merchant-information__skeleton-row" key={index}>
          <span />
          <span />
        </div>
      ))}
    </div>
  );
}

function TextInput({ label, value, error = "", type = "text", onChange }) {
  return (
    <label className="new-merchant-information__field">
      <span>{label}</span>
      <input
        type={type}
        value={value}
        onChange={onChange}
      />
      {error ? <small className="new-merchant-information__field-error">{error}</small> : null}
    </label>
  );
}

export default function MerchantDetailsPanel({
  merchant,
  merchantLoading = false,
  accessPoints,
  onOpenQrAfterGenerate,
  onEditPaymentConfig,
  merchantTabs = [],
  activeMerchantId = "",
  onMerchantChange,
  canAddMerchant = false,
  onAddMerchant,
}) {
  const dispatch = useDispatch();
  const entityType = sessionStorage.getItem("EntityType") || "";
  const entityRole = sessionStorage.getItem("EntityRole") || "";
  const isLegalEntity = entityType === "LEGALENTITY";
  const isMerchantEmployee = entityType === "MERCHANTEMPLOYEE";
  const isLegalAdmin = isLegalEntity && entityRole.includes("ADMIN");
  const [editMode, setEditMode] = useState(false);
  const [payload, setPayload] = useState({});
  const [errors, setErrors] = useState({});
  const [paymentData, setPaymentData] = useState({
    MDR: 0,
    PaymentConfigUSD: {
      CurrencyId: 1,
      CashPaymentAtBOBAdditionalFees: 0,
      IsCashPaymentAtBOBEnabled: true,
      PaymentByCardAdditionalFees: 0,
      IsPaymentByCardEnabled: false,
      OnCustomer: true,
    },
    PaymentConfigLBP: {
      CurrencyId: 2,
      CashPaymentAtBOBAdditionalFees: 0,
      IsCashPaymentAtBOBEnabled: true,
      PaymentByCardAdditionalFees: 0,
      IsPaymentByCardEnabled: true,
      OnCustomer: true,
    },
  });

  const generalInfo = merchant?.MerchantGeneralInfo || {};
  const profile = merchant?.MerchantProfile || {};
  const {
    LimitPerTransactionInUSD,
    LimitPerTransactionInLBP,
    IsAutoExchangeOnUSD,
    IsAutoExchangeOnLBP,
    ...restGeneralInfo
  } = generalInfo;
  const {
    LogoImage,
    CoverImage,
    ...profileInfo
  } = profile;

  const canToggleConversion = roleAuth("merchantinformation", accessPoints?.conversion);
  const canSetLimits = roleAuth("merchantinformation", accessPoints?.setLimits);
  const canEditPicture = roleAuth("merchantinformation", accessPoints?.editPicture);
  const canEditProfile = roleAuth("merchantinformation", accessPoints?.editProfile);
  const canEditPaymentInfo = roleAuth("merchantinformation", accessPoints?.editPaymentInfo);
  const showMerchantTabs = merchantTabs.length > 0;

  useEffect(() => {
    setPayload({
      MerchantID: merchant?.MerchantID,
      LimitPerTransactionInUSD,
      LimitPerTransactionInLBP,
      NewCoverImage: null,
      NewLogoImage: null,
      MerchantDisplayedName: profileInfo?.MerchantDisplayedName || "",
      Website: profileInfo?.Website || "",
      FacebookAccount: profileInfo?.Facebook || "",
      InstagramAccount: profileInfo?.Instagram || "",
    });

    setPaymentData({
      PaymentConfigUSD: merchant?.PaymentConfigUsd || {
        CurrencyId: 1,
      },
      PaymentConfigLBP: merchant?.PaymentConfigLbp || {
        CurrencyId: 2,
      },
      MDR: merchant?.MDR || 0,
    });
  }, [
    LimitPerTransactionInLBP,
    LimitPerTransactionInUSD,
    merchant,
    profileInfo?.Facebook,
    profileInfo?.Instagram,
    profileInfo?.MerchantDisplayedName,
    profileInfo?.Website,
  ]);

  useEffect(() => {
    setEditMode(false);
    setErrors({});
  }, [merchant?.MerchantID]);

  useEffect(() => {
    const saveConfig = async (configKey) => {
      const config = paymentData?.[configKey];
      if (!config?.changed || !merchant?.MerchantID) return;

      try {
        await onEditPaymentConfig?.(merchant.MerchantID, config);
      } catch {
        // keep legacy silent failure behavior here; global reload/flag stays unchanged
      } finally {
        setPaymentData((current) => ({
          ...current,
          [configKey]: {
            ...current[configKey],
            changed: false,
          },
        }));
      }
    };

    void saveConfig("PaymentConfigUSD");
    void saveConfig("PaymentConfigLBP");
  }, [merchant?.MerchantID, paymentData]);

  const profileViewData = useMemo(
    () => ({
      MerchantDisplayedName: profileInfo?.MerchantDisplayedName,
      Website: profileInfo?.Website,
      Facebook: profileInfo?.Facebook,
      Instagram: profileInfo?.Instagram,
    }),
    [
      profileInfo?.Facebook,
      profileInfo?.Instagram,
      profileInfo?.MerchantDisplayedName,
      profileInfo?.Website,
    ],
  );

  const validate = () => {
    const nextErrors = {
      LimitPerTransactionInUSD:
        payload?.LimitPerTransactionInUSD &&
        Number(payload.LimitPerTransactionInUSD) > 1
          ? ""
          : "Limit Per Transaction (USD) should be greater than 1",
      LimitPerTransactionInLBP:
        payload?.LimitPerTransactionInLBP &&
        Number(payload.LimitPerTransactionInLBP) > 1
          ? ""
          : "Limit Per Transaction (LBP) should be greater than 1",
      MerchantDisplayedName: payload?.MerchantDisplayedName
        ? ""
        : "Merchant Name is required",
    };

    setErrors(nextErrors);
    return Object.values(nextErrors).every((value) => value === "");
  };

  const handleSave = () => {
    if (!validate()) return;

    setEditMode(false);
    setErrors({});
    dispatch(setActionType(ACTION_TYPES.EDIT_MERCHANT_INFO));
    dispatch(setActionPayload(payload));
    onOpenQrAfterGenerate?.();
  };

  const handleAutoConversionToggle = (convertToCurrency) => {
    if (entityRole !== "SUPERADMIN") return;

    dispatch(setActionType(ACTION_TYPES.MANAGE_AUTO_CONVERSION));
    dispatch(
      setActionPayload({
        MerchantAccountID: merchant?.MerchantID,
        ConvertToUsd:
          convertToCurrency === "USD" ? !IsAutoExchangeOnUSD : IsAutoExchangeOnUSD,
        ConvertToLbp:
          convertToCurrency === "LBP" ? !IsAutoExchangeOnLBP : IsAutoExchangeOnLBP,
      }),
    );
    onOpenQrAfterGenerate?.();
  };

  if (merchantLoading) {
    return (
      <div className="new-merchant-information__content-stack">
        <section className="new-merchant-information__section-card">
          {showMerchantTabs ? (
            <div className="new-merchant-information__merchant-switcher">
              <div className="new-merchant-information__merchant-header">
                <div>
                  <span className="new-merchant-information__eyebrow">Merchants</span>
                  <h3>Merchant Profiles</h3>
                  <p>Switch between available merchants and manage each one independently.</p>
                </div>

                {canAddMerchant ? (
                  <button
                    type="button"
                    className="new-merchant-information__primary-button"
                    onClick={onAddMerchant}
                  >
                    Add Merchant
                  </button>
                ) : null}
              </div>

              <SectionTabs
                tabs={merchantTabs}
                activeTab={activeMerchantId}
                onChange={onMerchantChange}
                ariaLabel="Merchants"
                className="new-merchant-information__merchant-tabs"
              />
            </div>
          ) : null}

          <div className="new-merchant-information__section-head">
            <div>
              <span className="new-merchant-information__eyebrow">Merchant</span>
              <h3>Merchant Information</h3>
            </div>
          </div>
          <SkeletonRows />
        </section>
        <section className="new-merchant-information__section-card">
          <div className="new-merchant-information__section-head">
            <div>
              <span className="new-merchant-information__eyebrow">Profile</span>
              <h3>Merchant Profile</h3>
            </div>
          </div>
          <SkeletonRows />
        </section>
      </div>
    );
  }

  return (
    <div className="new-merchant-information__content-stack">
       {showMerchantTabs ? (
          <div className="new-merchant-information__merchant-switcher">
            <div className="new-merchant-information__merchant-header">
              <div>
              
                <h3>Merchants</h3>
                <p>Switch between available merchants and manage each one independently.</p>
              </div>

              {canAddMerchant ? (
                <button
                  type="button"
                  className="new-merchant-information__primary-button"
                  onClick={onAddMerchant}
                >
                 + Add Merchant
                </button>
              ) : null}
            </div>

            <SectionTabs
              tabs={merchantTabs}
              activeTab={activeMerchantId}
              onChange={onMerchantChange}
              ariaLabel="Merchants"
              className="new-merchant-information__merchant-tabs"
            />
          </div>
        ) : null}

      <section className="new-merchant-information__section-card">
       
        <div className="new-merchant-information__section-head">
          <div className="merchant_logo_div">
            <div>
           <img src="Images/MerchantImage.png"/>
           </div>
           <div> <h3>{generalInfo?.MerchantLegalName || ""}</h3></div>
            <p></p>
          </div>

          {isLegalAdmin ? (
            <button
              type="button"
              className={`new-merchant-information__action-button ${
                editMode ? "is-primary" : ""
              }`}
              onClick={() => {
                if (editMode) {
                  handleSave();
                } else {
                  setEditMode(true);
                }
              }}
            >
              {editMode ? "Save" : "Edit"}
            </button>
          ) : null}
        </div>

        <InfoRows data={restGeneralInfo} />

        {isLegalAdmin ? (
          <div className="new-merchant-information__settings-block">
            {canToggleConversion ? (
              <div className="new-merchant-information__toggle-stack">
                <AutoConversionControl
                  title="Auto Conversion to USD"
                  description="Convert LBP to USD as payments settle."
                  checked={Boolean(IsAutoExchangeOnUSD)}
                  disabled={Boolean(IsAutoExchangeOnLBP)}
                  onToggle={() => handleAutoConversionToggle("USD")}
                />
                <AutoConversionControl
                  title="Auto Conversion to LBP"
                  description="Convert USD to LBP as payments settle."
                  checked={Boolean(IsAutoExchangeOnLBP)}
                  disabled={Boolean(IsAutoExchangeOnUSD)}
                  onToggle={() => handleAutoConversionToggle("LBP")}
                />
              </div>
            ) : null}

            {!editMode ? (
              <div className="new-merchant-information__limits-grid">
                <div className="new-merchant-information__stat-card">
                  <span>Limit Per Transaction (USD)</span>
                  <strong>{formatAccount(LimitPerTransactionInUSD, 1)}</strong>
                </div>
                <div className="new-merchant-information__stat-card">
                  <span>Limit Per Transaction (LBP)</span>
                  <strong>{formatAccount(LimitPerTransactionInLBP, 2)}</strong>
                </div>
              </div>
            ) : canSetLimits ? (
              <div className="new-merchant-information__fields-grid">
                <TextInput
                  label="Limit Per Transaction (USD)"
                  type="number"
                  value={payload?.LimitPerTransactionInUSD ?? ""}
                  error={errors?.LimitPerTransactionInUSD}
                  onChange={(event) => {
                    setPayload((current) => ({
                      ...current,
                      LimitPerTransactionInUSD: event.target.value,
                    }));
                    setErrors((current) => ({
                      ...current,
                      LimitPerTransactionInUSD: "",
                    }));
                  }}
                />
                <TextInput
                  label="Limit Per Transaction (LBP)"
                  type="number"
                  value={payload?.LimitPerTransactionInLBP ?? ""}
                  error={errors?.LimitPerTransactionInLBP}
                  onChange={(event) => {
                    setPayload((current) => ({
                      ...current,
                      LimitPerTransactionInLBP: event.target.value,
                    }));
                    setErrors((current) => ({
                      ...current,
                      LimitPerTransactionInLBP: "",
                    }));
                  }}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </section>

      {isLegalEntity && canEditPaymentInfo ? (
        <PaymentFeesOptions data={paymentData} setData={setPaymentData} />
      ) : null}

      <section className="new-merchant-information__section-card">
        <div className="new-merchant-information__section-head">
          <div>
            {/* <span className="new-merchant-information__eyebrow">Profile</span> */}
            <h3>Merchant Profile</h3>
            <p>Manage branding assets and the merchant-facing profile details.</p>
          </div>

          {isMerchantEmployee ? (
            <button
              type="button"
              className={`new-merchant-information__action-button ${
                editMode ? "is-primary" : ""
              }`}
              onClick={() => {
                if (editMode) {
                  handleSave();
                } else {
                  setEditMode(true);
                }
              }}
            >
              {editMode ? "Save" : "Edit"}
            </button>
          ) : null}
        </div>

        {!editMode ? (
          <>
            <div className="new-merchant-information__media-grid">
              <div className="new-merchant-information__media-card is-logo">
                {LogoImage ? <img src={LogoImage} alt="Merchant logo" /> : <span>No logo</span>}
              </div>
              <div className="new-merchant-information__media-card is-cover">
                {CoverImage ? <img src={CoverImage} alt="Merchant cover" /> : <span>No cover image</span>}
              </div>
            </div>
            <InfoRows data={profileViewData} />
          </>
        ) : (
          <div className="new-merchant-information__edit-stack">
            {canEditPicture || isMerchantEmployee ? (
              <ImagePicker
                logoPreview={LogoImage}
                coverPreview={CoverImage}
                onLogoChange={({ name, dataUrl }) => {
                  setPayload((current) => ({
                    ...current,
                    NewLogoImage: dataUrl,
                    NewLogoImageName: name,
                  }));
                }}
                onCoverChange={({ name, dataUrl }) => {
                  setPayload((current) => ({
                    ...current,
                    NewCoverImage: dataUrl,
                    NewCoverImageName: name,
                  }));
                }}
              />
            ) : null}

            {canEditProfile || isMerchantEmployee ? (
              <div className="new-merchant-information__fields-grid is-profile">
                <TextInput
                  label="Merchant Displayed Name"
                  value={payload?.MerchantDisplayedName ?? ""}
                  error={errors?.MerchantDisplayedName}
                  onChange={(event) => {
                    setPayload((current) => ({
                      ...current,
                      MerchantDisplayedName: event.target.value,
                    }));
                    setErrors((current) => ({
                      ...current,
                      MerchantDisplayedName: "",
                    }));
                  }}
                />
                <TextInput
                  label="Website"
                  value={payload?.Website ?? ""}
                  onChange={(event) => {
                    setPayload((current) => ({
                      ...current,
                      Website: event.target.value,
                    }));
                  }}
                />
                <TextInput
                  label="Facebook"
                  value={payload?.FacebookAccount ?? ""}
                  onChange={(event) => {
                    setPayload((current) => ({
                      ...current,
                      FacebookAccount: event.target.value,
                    }));
                  }}
                />
                <TextInput
                  label="Instagram"
                  value={payload?.InstagramAccount ?? ""}
                  onChange={(event) => {
                    setPayload((current) => ({
                      ...current,
                      InstagramAccount: event.target.value,
                    }));
                  }}
                />
              </div>
            ) : null}
          </div>
        )}
      </section>
    </div>
  );
}
